import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useSEO from '../hooks/useSEO';

const Register = () => {

  useSEO({
    title: 'Create Your SERPfinity Account | Start Free SEO & AEO Analysis',
    description: 'Sign up for SERPfinity to access AI-powered SEO and AEO tools. Get your free AI search visibility score and optimize content.',
    canonical: 'https://thedigitalhall.club/register',
    robots: 'noindex, nofollow'
  });

  const navigate = useNavigate();
  const { register: registerUser, loading } = useAuth();
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        window.onRecaptchaChange = (token) => setRecaptchaToken(token);
        window.onRecaptchaExpired = () => setRecaptchaToken(null);
        return () => { document.head.removeChild(script); };
    }, []);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    company: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!recaptchaToken) {
      alert('Please complete the reCAPTCHA verification.');
      return;
    }
    const result = await registerUser({ ...formData, recaptchaToken });
    if (result.success) {
      navigate('/dashboard');
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Your SERPfinity Account | Start Free SEO &amp; AEO Analysis</title>
        <meta name="description" content="Sign up for SERPfinity to access AI-powered SEO and AEO tools. Get your free AI search visibility score, track rankings, and optimize content for ChatGPT, Google AI, and more." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://thedigitalhall.club/register" />
      </Helmet>
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              sign in to existing account
            </Link>
          </p>
        </div>
            {/* Introductory Price Lock-In Banner */}
            <div role="banner" aria-label="Limited-time introductory pricing offer" className="mt-4 mb-2 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 rounded-xl p-4 shadow-md">
              <p className="text-center font-bold text-gray-900 text-lg mb-1">
                <span aria-hidden="true">&#128293;</span> Lock In Your Introductory Price
              </p>
              <p className="text-center text-gray-700 text-sm">
                Sign up today and start your <strong>14-day free trial</strong> at our introductory Starter rate of <strong>$17/month</strong>. This price will increase soon as new features are added. Lock it in now and keep this rate for the life of your subscription.
              </p>
            </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                    autoComplete="given-name"
                required
                value={formData.firstName}
                onChange={handleChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="First name"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                    autoComplete="family-name"
                required
                value={formData.lastName}
                onChange={handleChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Last name"
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
                    autoComplete="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Email address"
            />
          </div>
          
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700">
              Company (optional)
            </label>
            <input
              id="company"
              name="company"
              type="text"
                    autoComplete="organization"
              value={formData.company}
              onChange={handleChange}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Company name"
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
                    autoComplete="new-password"
              required
              minLength={6}
              value={formData.password}
              onChange={handleChange}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Password (min. 6 characters)"
            />
          </div>

          <div>
            <div className="g-recaptcha" style={{display:'flex',justifyContent:'center',marginBottom:'16px'}} data-sitekey="6LeDS5gsAAAAAETfNlCn0a981RS6OqxTAfgqNbkH" data-callback="onRecaptchaChange" data-expired-callback="onRecaptchaExpired"></div>
          <button
              type="submit"
              disabled={loading || !recaptchaToken}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default Register;